import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PageBreadcrumb from "../../../common/PageBreadcrumb/PageBreadcrumb";
import ReportTable from "../../../common/ReportTable/ReportTable";
import Dropdown from "../../../common/dropDown";
import { EpsfAccessGate, useEpsfAccess } from "../epsfAccess";
import { findOption, renderImpProductCell } from "../epsfLedgerUi";
import { fetchImpProducts, saveImpProduct } from "../epsfService";
import ImpProductImportSheet from "./ImpProductImportSheet";
import {
  downloadImpProductExcelTemplate,
  emptyImpProductDraft,
  formatStorageTempRange,
  formatStrengthDisplay,
  parseImpProductsFromExcelFile,
} from "./impProductExcel";
import { getStorageTempBadgeTone } from "../epsfTempCompat";
import {
  plainTextToStorageHtml,
  storageHtmlToPlainText,
  getStorageInstructionsPlainMax,
} from "./impStorageInstructions";
import {
  validateImpProductDraft,
  validateImpImportDrafts,
  normalizeTempUnit,
} from "./impProductValidation";
import {
  DOSAGE_FORM_OPTIONS,
  ROUTE_OPTIONS,
  STRENGTH_UNIT_OPTIONS,
  TEMP_UNIT_OPTIONS,
  mergeLookupOptions,
} from "./impProductOptions";
import "../EpsfPages.scss";

const PRODUCT_NAME_MAX = 150;
const PRODUCT_CODE_MAX = 50;

/** Strip pasted/stored HTML tags from single-line product fields. */
function plainProductText(value, maxLen) {
  const plain = storageHtmlToPlainText(value)
    .replace(/\s+/g, " ")
    .trim();
  return maxLen ? plain.slice(0, maxLen) : plain;
}

const FIELD_OPTIONS = [
  { key: "Product", label: "IMP" },
  { key: "DosageStrength", label: "Dosage / strength" },
  { key: "Route", label: "Route" },
  { key: "StorageCondition", label: "Temperature" },
  { key: "Tracking", label: "Tracking" },
  { key: "Manufacturer", label: "Manufacturer" },
  { key: "Status", label: "Status" },
];

const DEFAULT_COLUMNS = [
  "Product",
  "DosageStrength",
  "Route",
  "StorageCondition",
  "Tracking",
  "Manufacturer",
  "Status",
];

function getDosageAndStrength(row) {
  const dosage = String(row?.DosageForm || row?.dosageForm || "").trim();
  const strength = formatStrengthDisplay(
    row?.StrengthValue ?? row?.strengthValue,
    row?.StrengthUnit || row?.strengthUnit,
    row?.Strength || row?.strength || "",
  ).trim();
  return { dosage, strength };
}

function formatDosageStrengthDisplay(row) {
  const { dosage, strength } = getDosageAndStrength(row);
  return [dosage, strength].filter(Boolean).join(" ") || "—";
}

const emptyForm = () => ({
  impProductId: "",
  ...emptyImpProductDraft(),
  isActive: true,
});

/** Next IMP-NNN for organisation catalogue (+ optional open drafts). */
function suggestNextImpCode(products, drafts = []) {
  let max = 0;
  const re = /^IMP-(\d+)$/i;
  const consider = (code) => {
    const match = String(code || "")
      .trim()
      .match(re);
    if (!match) return;
    const n = Number.parseInt(match[1], 10);
    if (Number.isFinite(n) && n > max) max = n;
  };
  (products || []).forEach((row) =>
    consider(row.ProductCode || row.productCode),
  );
  (drafts || []).forEach((d) => consider(d.productCode));
  return `IMP-${String(max + 1).padStart(3, "0")}`;
}

function makeBlankImportDraft(productCode = "") {
  return {
    id: `imp-row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    ...emptyImpProductDraft(),
    productCode: productCode || "",
    storageTempUnit: "C",
  };
}

function tempToInput(value) {
  if (value == null || value === "") return "";
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : "";
}

function parseRequiredNumber(value, { allowZero = false } = {}) {
  const text = String(value ?? "").trim();
  if (!text) return { ok: false, empty: true, value: null };
  const n = Number(text);
  if (!Number.isFinite(n)) return { ok: false, empty: false, value: null };
  if (!allowZero && n <= 0) return { ok: false, empty: false, value: n, nonPositive: true };
  return { ok: true, empty: false, value: n };
}

function parseTempNumber(value) {
  const text = String(value ?? "").trim();
  if (!text) return { ok: false, empty: true, value: null };
  const n = Number(text);
  if (!Number.isFinite(n)) return { ok: false, empty: false, value: null };
  return { ok: true, empty: false, value: n };
}

export default function ImpProducts() {
  const { loading, moduleAccess, canProductManage } = useEpsfAccess();
  const allow = moduleAccess && canProductManage;
  const excelInputRef = useRef(null);

  const [rows, setRows] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [createMode, setCreateMode] = useState("single"); // single | multiple
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [form, setForm] = useState(emptyForm);
  const [importMessage, setImportMessage] = useState("");
  const [importDrafts, setImportDrafts] = useState([]);
  const [importRowErrors, setImportRowErrors] = useState({});
  const [importParseErrors, setImportParseErrors] = useState([]);
  const [importFileName, setImportFileName] = useState("");
  const [importBusy, setImportBusy] = useState(false);
  const [extraDosageOptions, setExtraDosageOptions] = useState([]);
  const [extraRouteOptions, setExtraRouteOptions] = useState([]);
  const [extraStrengthUnitOptions, setExtraStrengthUnitOptions] = useState([]);
  const [otherModal, setOtherModal] = useState(null);

  const optionsFromRows = useMemo(() => {
    const dosage = [];
    const route = [];
    const unit = [];
    const seenD = new Set();
    const seenR = new Set();
    const seenU = new Set();
    (rows || []).forEach((row) => {
      const d = String(row.DosageForm || row.dosageForm || "").trim();
      const r = String(row.Route || row.route || "").trim();
      const u = String(row.StrengthUnit || row.strengthUnit || "").trim();
      if (d && d.toLowerCase() !== "other" && !seenD.has(d.toLowerCase())) {
        seenD.add(d.toLowerCase());
        dosage.push({ value: d, label: d });
      }
      if (r && r.toLowerCase() !== "other" && !seenR.has(r.toLowerCase())) {
        seenR.add(r.toLowerCase());
        route.push({ value: r, label: r });
      }
      if (u && u.toLowerCase() !== "other" && !seenU.has(u.toLowerCase())) {
        seenU.add(u.toLowerCase());
        unit.push({ value: u, label: u });
      }
    });
    return { dosage, route, unit };
  }, [rows]);

  const dosageOptions = useMemo(
    () =>
      mergeLookupOptions(
        DOSAGE_FORM_OPTIONS,
        [...optionsFromRows.dosage, ...extraDosageOptions],
        form.dosageForm,
      ),
    [optionsFromRows.dosage, extraDosageOptions, form.dosageForm],
  );
  const routeOptions = useMemo(
    () =>
      mergeLookupOptions(
        ROUTE_OPTIONS,
        [...optionsFromRows.route, ...extraRouteOptions],
        form.route,
      ),
    [optionsFromRows.route, extraRouteOptions, form.route],
  );
  const strengthUnitOptions = useMemo(
    () =>
      mergeLookupOptions(
        STRENGTH_UNIT_OPTIONS,
        [...optionsFromRows.unit, ...extraStrengthUnitOptions],
        form.strengthUnit,
      ),
    [optionsFromRows.unit, extraStrengthUnitOptions, form.strengthUnit],
  );

  const tempUnitSelected = useMemo(
    () => findOption(TEMP_UNIT_OPTIONS, form.storageTempUnit || "C"),
    [form.storageTempUnit],
  );

  const refresh = useCallback(async () => {
    setError("");
    setListLoading(true);
    try {
      const productResult = await fetchImpProducts(null, null, null);
      if (productResult.success) setRows(productResult.rows || []);
      else if (productResult.message) setError(productResult.message);
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openAdd = () => {
    setExtraDosageOptions([]);
    setExtraRouteOptions([]);
    setExtraStrengthUnitOptions([]);
    setForm({
      ...emptyForm(),
      productCode: suggestNextImpCode(rows),
    });
    setFormError("");
    setFieldErrors({});
    setError("");
    setCreateMode("single");
    setImportMessage("");
    setImportDrafts([]);
    setImportRowErrors({});
    setImportParseErrors([]);
    setImportFileName("");
    setShowModal(true);
  };

  const openEdit = (row) => {
    setExtraDosageOptions([]);
    setExtraRouteOptions([]);
    setExtraStrengthUnitOptions([]);
    setCreateMode("single");
    setImportDrafts([]);
    setImportRowErrors({});
    setImportParseErrors([]);
    setImportFileName("");
    setImportMessage("");
    setForm({
      impProductId: String(row.ImpProductId || row.impProductId || ""),
      productName: plainProductText(
        row.ProductName || row.productName || "",
        PRODUCT_NAME_MAX,
      ),
      productCode: row.ProductCode || row.productCode || "",
      dosageForm: row.DosageForm || row.dosageForm || "",
      strengthValue: tempToInput(row.StrengthValue ?? row.strengthValue),
      strengthUnit: row.StrengthUnit || row.strengthUnit || "",
      route: row.Route || row.route || "",
      manufacturer: row.Manufacturer || row.manufacturer || "",
      storageTempMin: tempToInput(row.StorageTempMin ?? row.storageTempMin),
      storageTempMax: tempToInput(row.StorageTempMax ?? row.storageTempMax),
      storageTempUnit: normalizeTempUnit(
        row.StorageTempUnit || row.storageTempUnit || "C",
      ) || "C",
      storageInstructions: storageHtmlToPlainText(
        row.StorageInstructions || row.storageInstructions || "",
      ),
      inventoryTrackingMethod: "IndividualKit",
      isActive: row.IsActive !== false && row.isActive !== false,
    });
    setFormError("");
    setFieldErrors({});
    setError("");
    setShowModal(true);
  };

  const resetImportState = () => {
    setImportMessage("");
    setImportDrafts([]);
    setImportRowErrors({});
    setImportParseErrors([]);
    setImportFileName("");
  };

  const closeModal = () => {
    if (busy || importBusy) return;
    setShowModal(false);
    setFormError("");
    setFieldErrors({});
    setCreateMode("single");
    resetImportState();
  };

  const seedMultipleDrafts = (existingDrafts = []) => {
    if (existingDrafts.length) return existingDrafts;
    return [makeBlankImportDraft(suggestNextImpCode(rows))];
  };

  const switchCreateMode = (mode) => {
    if (busy || importBusy || form.impProductId) return;
    if (mode === createMode) return;
    setCreateMode(mode);
    setFormError("");
    setFieldErrors({});
    setImportMessage("");
    if (mode === "multiple") {
      setImportDrafts((prev) => {
        const next = seedMultipleDrafts(prev);
        setImportRowErrors(validateImpImportDrafts(next, rows));
        return next;
      });
      setImportParseErrors([]);
    } else {
      setForm({
        ...emptyForm(),
        productCode: suggestNextImpCode(rows, importDrafts),
      });
    }
  };

  const openImport = () => {
    setForm(emptyForm());
    setFormError("");
    setFieldErrors({});
    setError("");
    setCreateMode("multiple");
    resetImportState();
    const draft = makeBlankImportDraft(suggestNextImpCode(rows));
    setImportDrafts([draft]);
    setImportRowErrors(validateImpImportDrafts([draft], rows));
    setShowModal(true);
  };

  const addImportRow = () => {
    setImportDrafts((prev) => {
      const draft = makeBlankImportDraft(suggestNextImpCode(rows, prev));
      const next = [...prev, draft];
      setImportRowErrors(validateImpImportDrafts(next, rows));
      return next;
    });
  };

  const buildSavePayload = (draft, impProductId = null) => {
    const minParsed = parseTempNumber(draft.storageTempMin);
    const maxParsed = parseTempNumber(draft.storageTempMax);
    const strengthParsed = parseRequiredNumber(draft.strengthValue, {
      allowZero: false,
    });
    const strengthEmpty = !String(draft.strengthValue || "").trim();
    const unitEmpty = !String(draft.strengthUnit || "").trim();
    return {
      ImpProductId: impProductId || null,
      ProductCode: plainProductText(draft.productCode || "", PRODUCT_CODE_MAX),
      ProductName: plainProductText(draft.productName || "", PRODUCT_NAME_MAX),
      DosageForm: String(draft.dosageForm || "").trim(),
      StrengthValue:
        strengthEmpty && unitEmpty ? null : strengthParsed.value,
      StrengthUnit: unitEmpty ? null : String(draft.strengthUnit || "").trim(),
      Route: String(draft.route || "").trim(),
      Manufacturer: String(draft.manufacturer || "").trim() || null,
      StorageTempMin: minParsed.value,
      StorageTempMax: maxParsed.value,
      StorageTempUnit: normalizeTempUnit(draft.storageTempUnit) || "C",
      StorageInstructions: plainTextToStorageHtml(draft.storageInstructions),
      InventoryTrackingMethod: "IndividualKit",
      IsActive: draft.isActive !== false,
    };
  };

  const saveDraftList = async (drafts) => {
    const list = Array.isArray(drafts) ? drafts : [];
    if (!list.length) return { ok: false, message: "No IMP products to save." };

    const batchCodes = new Map();
    list.forEach((draft, i) => {
      const code = String(draft.productCode || "")
        .trim()
        .toLowerCase();
      if (code && !batchCodes.has(code)) batchCodes.set(code, i);
    });

    for (let i = 0; i < list.length; i += 1) {
      const draft = list[i];
      const errors = validateImpProductDraft(draft, {
        existingRows: rows,
        batchCodes,
        currentBatchIndex: i,
      });
      if (Object.keys(errors).length) {
        return {
          ok: false,
          message:
            errors.productCode ||
            `Row ${i + 1} (${draft.productCode || "IMP"}): ${
              Object.values(errors)[0]
            }`,
          saved: 0,
        };
      }
    }

    let saved = 0;
    for (const draft of list) {
      const payload = buildSavePayload({ ...draft, isActive: true });
      if (!payload.ProductCode || !payload.ProductName) continue;
      const result = await saveImpProduct(payload);
      if (!result.success) {
        return {
          ok: false,
          message: result.message || `Could not save ${payload.ProductCode}.`,
          saved,
        };
      }
      saved += 1;
    }
    return {
      ok: true,
      message: `Saved ${saved} IMP product${saved === 1 ? "" : "s"}.`,
      saved,
    };
  };

  const validateForm = () =>
    validateImpProductDraft(
      { ...form, inventoryTrackingMethod: "IndividualKit" },
      {
        existingRows: rows,
        excludeImpProductId: form.impProductId || null,
      },
    );

  const onSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    const errors = validateForm();
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;

    setBusy(true);
    try {
      const result = await saveImpProduct(
        buildSavePayload(form, form.impProductId || null),
      );
      if (!result.success) {
        setFormError(result.message || "Could not save IMP.");
        return;
      }
      setShowModal(false);
      setForm(emptyForm());
      setFieldErrors({});
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const onExcelSelected = async (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file) return;
    setImportBusy(true);
    setImportMessage("");
    try {
      const result = await parseImpProductsFromExcelFile(file);
      const drafts = result.drafts || [];
      setImportDrafts(drafts);
      setImportRowErrors(validateImpImportDrafts(drafts, rows));
      setImportParseErrors(result.parseErrors || []);
      setImportFileName(file.name || "");
      setImportMessage(result.message || "");
    } catch (err) {
      setImportDrafts([]);
      setImportRowErrors({});
      setImportParseErrors([]);
      setImportFileName("");
      setImportMessage(err?.message || "Could not read the Excel file.");
    } finally {
      setImportBusy(false);
    }
  };

  const updateImportDraft = (id, updater) => {
    setImportDrafts((prev) => {
      const next = prev.map((d) => (d.id === id ? updater(d) : d));
      setImportRowErrors(validateImpImportDrafts(next, rows));
      return next;
    });
  };

  const removeImportDraft = (id) => {
    setImportDrafts((prev) => {
      const next = prev.filter((d) => d.id !== id);
      setImportRowErrors(validateImpImportDrafts(next, rows));
      return next;
    });
  };

  const onSaveImportDrafts = async () => {
    setImportBusy(true);
    setImportMessage("");
    try {
      const rowErrors = validateImpImportDrafts(importDrafts, rows);
      setImportRowErrors(rowErrors);
      if (Object.keys(rowErrors).length) {
        setImportMessage(
          Object.values(rowErrors)[0] ||
            "Fix validation errors in the grid before saving.",
        );
        return;
      }
      const result = await saveDraftList(importDrafts);
      setImportMessage(result.message || "");
      if (result.ok) {
        setShowModal(false);
        setCreateMode("single");
        setImportDrafts([]);
        setImportRowErrors({});
        setImportParseErrors([]);
        setImportFileName("");
        await refresh();
      }
    } finally {
      setImportBusy(false);
    }
  };

  const setField = (key) => (ev) => {
    let value = ev.target.value;
    if (key === "productName") {
      value = plainProductText(value, PRODUCT_NAME_MAX);
    } else if (key === "productCode") {
      value = plainProductText(value, PRODUCT_CODE_MAX);
    } else if (key === "manufacturer") {
      value = plainProductText(value, PRODUCT_NAME_MAX);
    }
    setForm((prev) => ({ ...prev, [key]: value }));
    if (fieldErrors[key]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const OTHER_VALUE = "Other";

  const OTHER_FIELD_META = {
    dosageForm: {
      title: "Add dosage form",
      label: "Dosage form",
      placeholder: "e.g. Softgel Capsule",
      maxLength: 80,
    },
    route: {
      title: "Add route of administration",
      label: "Route of administration",
      placeholder: "e.g. Epidural",
      maxLength: 80,
    },
    strengthUnit: {
      title: "Add strength unit",
      label: "Strength unit",
      placeholder: "e.g. mg/g",
      maxLength: 40,
    },
  };

  const addExtraOption = (key, value) => {
    const nextOpt = { value, label: value };
    const same = (prev) =>
      prev.some((x) => String(x.value).toLowerCase() === value.toLowerCase())
        ? prev
        : [...prev, nextOpt];
    if (key === "dosageForm") setExtraDosageOptions(same);
    else if (key === "route") setExtraRouteOptions(same);
    else if (key === "strengthUnit") setExtraStrengthUnitOptions(same);
  };

  const setSelectField = (key) => (opt) => {
    const value = opt?.value || "";
    if (value === OTHER_VALUE) {
      const meta = OTHER_FIELD_META[key];
      if (!meta) return;
      setOtherModal({
        field: key,
        draft: "",
        error: "",
        previousValue: form[key] || "",
        ...meta,
      });
      return;
    }
    setForm((prev) => ({ ...prev, [key]: value }));
    if (fieldErrors[key]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const closeOtherModal = () => setOtherModal(null);

  const findExistingLookupOption = (field, typedValue) => {
    const needle = String(typedValue || "")
      .trim()
      .toLowerCase();
    if (!needle || needle === "other") return null;
    const list =
      field === "dosageForm"
        ? dosageOptions
        : field === "route"
          ? routeOptions
          : field === "strengthUnit"
            ? strengthUnitOptions
            : [];
    return (
      (list || []).find((opt) => {
        const v = String(opt?.value ?? "")
          .trim()
          .toLowerCase();
        const l = String(opt?.label ?? "")
          .trim()
          .toLowerCase();
        if (!v || v === "other") return false;
        return v === needle || l === needle;
      }) || null
    );
  };

  const confirmOtherModal = () => {
    if (!otherModal) return;
    const typed = String(otherModal.draft || "").trim();
    const max = otherModal.maxLength || 80;
    if (!typed) {
      setOtherModal((prev) =>
        prev ? { ...prev, error: `${prev.label} is required.` } : prev,
      );
      return;
    }
    if (typed.toLowerCase() === "other") {
      setOtherModal((prev) =>
        prev
          ? { ...prev, error: "Please enter a specific value, not Other." }
          : prev,
      );
      return;
    }
    if (typed.length > max) {
      setOtherModal((prev) =>
        prev
          ? {
              ...prev,
              error: `${prev.label} cannot exceed ${max} characters.`,
            }
          : prev,
      );
      return;
    }

    const existing = findExistingLookupOption(otherModal.field, typed);
    const finalValue = existing
      ? String(existing.value).trim()
      : typed;

    // Already in the list → silently select that option (no duplicate entry).
    if (!existing) {
      addExtraOption(otherModal.field, finalValue);
    }

    setForm((prev) => ({ ...prev, [otherModal.field]: finalValue }));
    if (fieldErrors[otherModal.field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[otherModal.field];
        return next;
      });
    }
    setOtherModal(null);
  };

  const dosageSelected =
    otherModal?.field === "dosageForm"
      ? { value: OTHER_VALUE, label: "Other" }
      : findOption(dosageOptions, form.dosageForm);
  const routeSelected =
    otherModal?.field === "route"
      ? { value: OTHER_VALUE, label: "Other" }
      : findOption(routeOptions, form.route);
  const strengthUnitSelected =
    otherModal?.field === "strengthUnit"
      ? { value: OTHER_VALUE, label: "Other" }
      : findOption(strengthUnitOptions, form.strengthUnit);

  const canManage = Boolean(canProductManage);

  const renderCell = useCallback(
    (row, field) => {
      if (field === "Product") {
        if (!canManage) {
          return renderImpProductCell(row);
        }
        return renderImpProductCell(row, {
          as: "button",
          type: "button",
          className: "report-table__cell-link",
          title: "Edit IMP",
          onClick: () => openEdit(row),
        });
      }
      if (field === "DosageStrength") {
        const { dosage, strength } = getDosageAndStrength(row);
        if (!dosage && !strength) return "—";
        return (
          <div
            className="epsf-imp-product-cell"
            title={[dosage, strength].filter(Boolean).join(" ")}
          >
            <span className="epsf-imp-product-cell__name">
              {dosage || "—"}
            </span>
            {strength ? (
              <span className="epsf-imp-product-cell__code">{strength}</span>
            ) : null}
          </div>
        );
      }
      if (field === "Route") return row.Route || row.route || "—";
      if (field === "Manufacturer") {
        return row.Manufacturer || row.manufacturer || "—";
      }
      if (field === "Tracking") {
        const method =
          row.InventoryTrackingMethod ||
          row.inventoryTrackingMethod ||
          "IndividualKit";
        return method === "IndividualKit"
          ? "Individual Kit"
          : "Batch / Quantity";
      }
      if (field === "StorageCondition") {
        const label =
          formatStorageTempRange(
            row.StorageTempMin ?? row.storageTempMin,
            row.StorageTempMax ?? row.storageTempMax,
            row.StorageTempUnit || row.storageTempUnit,
            row.StorageCondition || row.storageCondition || "",
          ) || "";
        if (!label) return "—";
        const tone = getStorageTempBadgeTone(
          row.StorageTempMin ?? row.storageTempMin,
          row.StorageTempMax ?? row.storageTempMax,
          row.StorageTempUnit || row.storageTempUnit,
        );
        return (
          <span
            className={`epsf-imp-pill epsf-imp-pill--temp-${tone}`}
            title={label}
          >
            {label}
          </span>
        );
      }
      if (field === "Status") {
        const active = row.IsActive !== false && row.isActive !== false;
        return (
          <span
            className={`epsf-imp-pill epsf-imp-pill--${
              active ? "active" : "inactive"
            }`}
          >
            {active ? "Active" : "Inactive"}
          </span>
        );
      }
      const value = row[field];
      return value == null || value === "" ? "—" : String(value);
    },
    [canManage],
  );

  const matchColumnSearch = useCallback((row, field, searchValue) => {
    const term = String(searchValue || "")
      .trim()
      .toLowerCase();
    if (!term) return true;
    if (field === "Product") {
      const name = String(row.ProductName || row.productName || "").toLowerCase();
      const code = String(row.ProductCode || row.productCode || "").toLowerCase();
      return name.includes(term) || code.includes(term);
    }
    if (field === "DosageStrength") {
      return formatDosageStrengthDisplay(row).toLowerCase().includes(term);
    }
    if (field === "StorageCondition") {
      const label = formatStorageTempRange(
        row.StorageTempMin ?? row.storageTempMin,
        row.StorageTempMax ?? row.storageTempMax,
        row.StorageTempUnit || row.storageTempUnit,
        row.StorageCondition || row.storageCondition || "",
      ).toLowerCase();
      return label.includes(term);
    }
    return String(row[field] ?? "")
      .toLowerCase()
      .includes(term);
  }, []);

  const isEditMode = Boolean(form.impProductId);
  const isMultipleMode = !isEditMode && createMode === "multiple";

  const importErrorCount = Object.keys(importRowErrors).length;
  const importHasRowErrors = importErrorCount > 0;
  const importCanSave =
    !importBusy &&
    importDrafts.length > 0 &&
    !importHasRowErrors &&
    importParseErrors.length === 0;

  return (
    <EpsfAccessGate allow={allow} loading={loading}>
      <div className="epsf-list-page">
        <PageBreadcrumb
          items={[
            { label: "ePSF", path: "/epsf/imp/inventory" },
            { label: "IMP Products" },
          ]}
          actions={[
            { icon: "fas fa-refresh", label: "Refresh", onClick: refresh },
            ...(canManage
              ? [
                  {
                    icon: "fas fa-file-excel",
                    label: "Import Excel",
                    onClick: openImport,
                  },
                  { icon: "fas fa-plus", label: "Add IMP", onClick: openAdd },
                ]
              : []),
          ]}
        />

        {error ? <p className="epsf-list-page__error">{error}</p> : null}

        <div className="epsf-report-wrap">
          <ReportTable
            rows={rows}
            loading={listLoading}
            fieldOptions={FIELD_OPTIONS}
            defaultColumns={DEFAULT_COLUMNS}
            getFieldLabel={(field) =>
              FIELD_OPTIONS.find((x) => x.key === field)?.label || field
            }
            renderCell={renderCell}
            matchColumnSearch={matchColumnSearch}
            getRowKey={(row, index) =>
              String(row.ImpProductId || row.impProductId || index)
            }
            persistLayoutKey="epsf-imp-products-v7"
            emptyMessage="No IMP products yet for this organisation."
            pageSizeOptions={[10, 25, 50, 100]}
            columnSearchMode="client"
          />
        </div>

        {showModal ? (
          <div
            className={`modal fade show d-block epsf-modal${
              isMultipleMode ? " epsf-imp-import-modal" : ""
            }`}
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            style={
              isMultipleMode
                ? undefined
                : { backgroundColor: "rgba(0,0,0,0.5)" }
            }
          >
            <div
              className={
                isMultipleMode
                  ? "modal-dialog epsf-imp-import-modal__dialog"
                  : "modal-dialog modal-dialog-centered modal-lg"
              }
            >
              <div
                className={
                  isMultipleMode
                    ? "modal-content epsf-imp-import-modal__content"
                    : "modal-content"
                }
              >
                <div
                  className={
                    isMultipleMode
                      ? "modal-header epsf-imp-import-modal__header"
                      : "modal-header"
                  }
                >
                  <h5 className="modal-title mb-0">
                    {isEditMode
                      ? "Edit IMP"
                      : isMultipleMode
                        ? "Add IMP products"
                        : "Add IMP"}
                  </h5>
                  {!isEditMode ? (
                    <div
                      className="epsf-imp-create-mode"
                      role="tablist"
                      aria-label="Create mode"
                    >
                      <button
                        type="button"
                        role="tab"
                        aria-selected={createMode === "single"}
                        className={`epsf-imp-create-mode__btn${
                          createMode === "single" ? " is-active" : ""
                        }`}
                        onClick={() => switchCreateMode("single")}
                        disabled={busy || importBusy}
                      >
                        Single
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={createMode === "multiple"}
                        className={`epsf-imp-create-mode__btn${
                          createMode === "multiple" ? " is-active" : ""
                        }`}
                        onClick={() => switchCreateMode("multiple")}
                        disabled={busy || importBusy}
                      >
                        Multiple
                      </button>
                    </div>
                  ) : null}
                  {isMultipleMode ? (
                    <div className="epsf-imp-import-modal__header-actions">
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={downloadImpProductExcelTemplate}
                        disabled={importBusy}
                      >
                        <i className="fas fa-download me-1" aria-hidden="true" />
                        Template
                      </button>
                      <label
                        className={`btn btn-outline-primary btn-sm mb-0${
                          importBusy ? " disabled" : ""
                        }`}
                      >
                        <i className="fas fa-upload me-1" aria-hidden="true" />
                        Upload Excel
                        <input
                          ref={excelInputRef}
                          type="file"
                          accept=".xlsx,.xls"
                          className="d-none"
                          onChange={onExcelSelected}
                          disabled={importBusy}
                        />
                      </label>
                      {importFileName ? (
                        <span
                          className="epsf-imp-import-sheet__file"
                          title={importFileName}
                        >
                          {importFileName}
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={closeModal}
                    disabled={busy || importBusy}
                  />
                </div>

                {isMultipleMode ? (
                  <>
                    <div className="modal-body epsf-imp-import-modal__body">
                      <ImpProductImportSheet
                        drafts={importDrafts}
                        errorsById={importRowErrors}
                        parseErrors={importParseErrors}
                        statusMessage={importMessage}
                        onUpdateDraft={updateImportDraft}
                        onRemoveDraft={removeImportDraft}
                        onAddRow={addImportRow}
                      />
                    </div>
                    <div className="modal-footer epsf-imp-import-modal__footer">
                      <div className="epsf-imp-import-modal__footer-meta">
                        {importDrafts.length > 0 ? (
                          <span className="epsf-imp-import-sheet__chip">
                            {importDrafts.length} row
                            {importDrafts.length === 1 ? "" : "s"}
                          </span>
                        ) : null}
                        {importErrorCount > 0 ? (
                          <span className="epsf-imp-import-sheet__chip epsf-imp-import-sheet__chip--warn">
                            {importErrorCount} to fix
                          </span>
                        ) : importDrafts.length > 0 &&
                          importParseErrors.length === 0 ? (
                          <span className="epsf-imp-import-sheet__chip epsf-imp-import-sheet__chip--ok">
                            Ready to save
                          </span>
                        ) : null}
                      </div>
                      <div className="epsf-imp-import-modal__footer-actions">
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={closeModal}
                          disabled={importBusy}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={onSaveImportDrafts}
                          disabled={!importCanSave}
                        >
                          {importBusy
                            ? "Saving..."
                            : `Save products${
                                importDrafts.length
                                  ? ` (${importDrafts.length})`
                                  : ""
                              }`}
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                <form onSubmit={onSubmit} noValidate>
                  <div className="modal-body">
                    <div className="epsf-imp-form">
                      <div className="row g-3">
                        {/* Row 1: code+name | dosage+route */}
                        <div className="col-12 col-md-6">
                          <div className="row g-2">
                            <div className="col-3">
                              <div className="epsf-form-field">
                                <label className="form-label">
                                  IMP code <span className="mandatory">*</span>
                                </label>
                                <input
                                  className={`form-control${
                                    fieldErrors.productCode ? " is-invalid" : ""
                                  }`}
                                  value={form.productCode}
                                  onChange={setField("productCode")}
                                  placeholder="e.g. IMP-001"
                                  maxLength={PRODUCT_CODE_MAX}
                                />
                                {fieldErrors.productCode ? (
                                  <div className="epsf-form-field-error">
                                    {fieldErrors.productCode}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                            <div className="col-9">
                              <div className="epsf-form-field">
                                <label className="form-label">
                                  IMP name <span className="mandatory">*</span>
                                </label>
                                <input
                                  className={`form-control${
                                    fieldErrors.productName ? " is-invalid" : ""
                                  }`}
                                  value={form.productName}
                                  onChange={setField("productName")}
                                  onPaste={(ev) => {
                                    const text = ev.clipboardData?.getData("text/plain");
                                    if (text == null) return;
                                    ev.preventDefault();
                                    const next = plainProductText(text, PRODUCT_NAME_MAX);
                                    setForm((prev) => ({ ...prev, productName: next }));
                                    if (fieldErrors.productName) {
                                      setFieldErrors((prev) => {
                                        const n = { ...prev };
                                        delete n.productName;
                                        return n;
                                      });
                                    }
                                  }}
                                  placeholder="e.g. Study Drug A"
                                  maxLength={PRODUCT_NAME_MAX}
                                  title={`Maximum ${PRODUCT_NAME_MAX} characters`}
                                />
                                {String(form.productName || "").length >=
                                PRODUCT_NAME_MAX ? (
                                  <div
                                    className="epsf-form-field-error"
                                    role="alert"
                                  >
                                    Maximum {PRODUCT_NAME_MAX} characters
                                    reached.
                                  </div>
                                ) : null}
                                {fieldErrors.productName ? (
                                  <div className="epsf-form-field-error">
                                    {fieldErrors.productName}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-12 col-md-6">
                          <div className="row g-2">
                            <div className="col-6">
                              <div className="epsf-form-field">
                                <label className="form-label">
                                  Dosage form{" "}
                                  <span className="mandatory">*</span>
                                </label>
                                <Dropdown
                                  options={dosageOptions}
                                  selected={dosageSelected}
                                  onSelect={setSelectField("dosageForm")}
                                  pinValues={["Other"]}
                                />
                                {fieldErrors.dosageForm ? (
                                  <div className="epsf-form-field-error">
                                    {fieldErrors.dosageForm}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                            <div className="col-6">
                              <div className="epsf-form-field">
                                <label className="form-label">
                                  Route of administration{" "}
                                  <span className="mandatory">*</span>
                                </label>
                                <Dropdown
                                  options={routeOptions}
                                  selected={routeSelected}
                                  onSelect={setSelectField("route")}
                                  pinValues={["Other"]}
                                />
                                {fieldErrors.route ? (
                                  <div className="epsf-form-field-error">
                                    {fieldErrors.route}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Row 2: strength | storage temperature */}
                        <div className="col-12 col-md-6">
                          <div className="row g-2">
                            <div className="col-8">
                              <div className="epsf-form-field">
                                <label className="form-label">Strength</label>
                                <input
                                  type="number"
                                  step="any"
                                  min="0"
                                  className={`form-control${
                                    fieldErrors.strengthValue
                                      ? " is-invalid"
                                      : ""
                                  }`}
                                  value={form.strengthValue}
                                  onChange={setField("strengthValue")}
                                  placeholder="e.g. 50"
                                  aria-label="Strength value"
                                />
                                {fieldErrors.strengthValue ? (
                                  <div className="epsf-form-field-error">
                                    {fieldErrors.strengthValue}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                            <div className="col-4">
                              <div className="epsf-form-field">
                                <label className="form-label">Unit</label>
                                <Dropdown
                                  options={strengthUnitOptions}
                                  selected={strengthUnitSelected}
                                  onSelect={setSelectField("strengthUnit")}
                                  pinValues={["Other"]}
                                />
                                {fieldErrors.strengthUnit ? (
                                  <div className="epsf-form-field-error">
                                    {fieldErrors.strengthUnit}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-12 col-md-6">
                          <div className="epsf-form-field">
                            <label className="form-label">
                              Storage temperature{" "}
                              <span className="mandatory">*</span>
                            </label>
                            <div className="row g-2">
                              <div className="col-4">
                                <Dropdown
                                  options={TEMP_UNIT_OPTIONS}
                                  selected={tempUnitSelected}
                                  onSelect={setSelectField("storageTempUnit")}
                                  showSearch={false}
                                />
                                {fieldErrors.storageTempUnit ? (
                                  <div className="epsf-form-field-error">
                                    {fieldErrors.storageTempUnit}
                                  </div>
                                ) : null}
                              </div>
                              <div className="col-4">
                                <input
                                  type="number"
                                  step="any"
                                  className={`form-control${
                                    fieldErrors.storageTempMin
                                      ? " is-invalid"
                                      : ""
                                  }`}
                                  value={form.storageTempMin}
                                  onChange={setField("storageTempMin")}
                                  placeholder="Min e.g. 2"
                                  aria-label="Minimum temperature"
                                />
                                {fieldErrors.storageTempMin ? (
                                  <div className="epsf-form-field-error">
                                    {fieldErrors.storageTempMin}
                                  </div>
                                ) : null}
                              </div>
                              <div className="col-4">
                                <input
                                  type="number"
                                  step="any"
                                  className={`form-control${
                                    fieldErrors.storageTempMax
                                      ? " is-invalid"
                                      : ""
                                  }`}
                                  value={form.storageTempMax}
                                  onChange={setField("storageTempMax")}
                                  placeholder="Max e.g. 8"
                                  aria-label="Maximum temperature"
                                />
                                {fieldErrors.storageTempMax ? (
                                  <div className="epsf-form-field-error">
                                    {fieldErrors.storageTempMax}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Row 3: manufacturer | (spacer) then full-width instructions */}
                        <div className="col-12 col-md-6">
                          <div className="epsf-form-field">
                            <label className="form-label">Manufacturer</label>
                            <input
                              className={`form-control${
                                fieldErrors.manufacturer ? " is-invalid" : ""
                              }`}
                              value={form.manufacturer}
                              onChange={setField("manufacturer")}
                              placeholder="e.g. ABC Pharma"
                              maxLength={150}
                            />
                            {fieldErrors.manufacturer ? (
                              <div className="epsf-form-field-error">
                                {fieldErrors.manufacturer}
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div className="col-12">
                          <div className="epsf-form-field">
                            <label className="form-label">
                              Storage instructions
                            </label>
                            <textarea
                              className={`form-control epsf-imp-form__instructions${
                                fieldErrors.storageInstructions
                                  ? " is-invalid"
                                  : ""
                              }`}
                              rows={3}
                              value={form.storageInstructions}
                              onChange={setField("storageInstructions")}
                              placeholder="e.g. Protect from light. Do not freeze."
                              maxLength={getStorageInstructionsPlainMax()}
                            />
                            {fieldErrors.storageInstructions ? (
                              <div className="epsf-form-field-error">
                                {fieldErrors.storageInstructions}
                              </div>
                            ) : null}
                          </div>
                        </div>

                        {form.impProductId ? (
                          <div className="col-12">
                            <div className="epsf-form-field">
                              <label className="form-label d-flex align-items-center gap-2 mb-0">
                                <input
                                  type="checkbox"
                                  checked={form.isActive}
                                  onChange={(ev) =>
                                    setForm((prev) => ({
                                      ...prev,
                                      isActive: ev.target.checked,
                                    }))
                                  }
                                />
                                <span>Active</span>
                              </label>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                    {formError ? (
                      <p className="epsf-list-page__error mt-2 mb-0">
                        {formError}
                      </p>
                    ) : null}
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={closeModal}
                      disabled={busy}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={busy}
                    >
                      {busy ? "Saving..." : "Save IMP"}
                    </button>
                  </div>
                </form>
                )}
              </div>
            </div>
          </div>
        ) : null}

        {otherModal ? (
          <div
            className="epsf-other-inline-overlay"
            onClick={closeOtherModal}
            role="presentation"
          >
            <div
              className="epsf-other-inline-card"
              role="dialog"
              aria-modal="true"
              aria-label={otherModal.title}
              onClick={(ev) => ev.stopPropagation()}
            >
              <div className="epsf-other-inline-card__head">
                <span className="epsf-other-inline-card__title">
                  {otherModal.title}
                </span>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={closeOtherModal}
                />
              </div>
              <div className="epsf-other-inline-card__body">
                <label className="form-label mb-1">
                  {otherModal.label} <span className="mandatory">*</span>
                </label>
                <input
                  className={`form-control${
                    otherModal.error ? " is-invalid" : ""
                  }`}
                  value={otherModal.draft}
                  autoFocus
                  maxLength={otherModal.maxLength}
                  placeholder={otherModal.placeholder}
                  onChange={(ev) =>
                    setOtherModal((prev) =>
                      prev
                        ? { ...prev, draft: ev.target.value, error: "" }
                        : prev,
                    )
                  }
                  onKeyDown={(ev) => {
                    if (ev.key === "Enter") {
                      ev.preventDefault();
                      confirmOtherModal();
                    }
                  }}
                />
                {otherModal.error ? (
                  <div className="epsf-form-field-error">{otherModal.error}</div>
                ) : null}
              </div>
              <div className="epsf-other-inline-card__foot">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={closeOtherModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={confirmOtherModal}
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </EpsfAccessGate>
  );
}
